import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
} from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore, ExpandLess, ShowChart } from '@mui/icons-material';
import IBKRSymbolChart, { Recommendation } from './IBKRSymbolChart';
import { ibkrService } from '../../services/ibkrService';
import { formatCurrency, formatValueWithNet } from '../../utils/formatValue';

interface SymbolAnalysis {
  symbol: string;
  price: number;
  price_net?: number;
  regime: string;
  regime_since_days?: number;
  score: number;
  iv_rank: number;
  iv: number;
  iv_range?: [number, number];
  explanation: string;
  recommendation?: Recommendation;
}

interface AnalysisData {
  updated_at?: string;
  symbols: SymbolAnalysis[];
}

const REGIME_COLORS: Record<string, 'success' | 'error' | 'default' | 'warning'> = {
  TREND_UP: 'success',
  TREND_DOWN: 'error',
  RANGE: 'default',
  VOLATILE: 'warning',
};

const REGIME_LABELS: Record<string, string> = {
  TREND_UP: 'TREND UP',
  TREND_DOWN: 'TREND DOWN',
  RANGE: 'RANGE',
  VOLATILE: 'VOLATILE',
};

const IBKRAnalysis: React.FC = () => {
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executingSymbol, setExecutingSymbol] = useState<string | null>(null);
  const [chartOpen, setChartOpen] = useState<Record<string, boolean>>({});

  const toggleChart = (symbol: string) =>
    setChartOpen((prev) => ({ ...prev, [symbol]: !prev[symbol] }));

  const fetchAnalysis = useCallback(async () => {
    try {
      setError(null);
      const data = await ibkrService.getAnalysis();
      // Backend liefert pro Symbol einen Eintrag: bei Erfolg ein Analyse-
      // Objekt mit Feld `symbol`, bei Symbol-spezifischem Fehler ein Objekt
      // {error: "..."}. Top-Level-Fehler hat makeRequest schon abgefangen.
      if (!data || typeof data !== 'object') {
        throw new Error('Unerwartete Antwort vom Server');
      }
      const symbolErrors: string[] = [];
      const raw: any[] = [];
      for (const [sym, value] of Object.entries(data as Record<string, any>)) {
        if (!value || typeof value !== 'object') continue;
        if (typeof value.error === 'string') {
          symbolErrors.push(`${sym}: ${value.error}`);
          continue;
        }
        if (value.symbol) raw.push(value);
      }
      const symbols = raw.map((s: any) => ({
        symbol: s.symbol,
        price: s.price ?? 0,
        price_net: s.price_net,
        regime: s.regime ?? 'RANGE',
        score: s.score ?? 0,
        iv_rank: s.iv_rank ?? 0,
        iv: (s.iv_current ?? 0) * 100,
        iv_range: [(s.iv_low ?? 0) * 100, (s.iv_high ?? 0) * 100] as [number, number],
        explanation: s.regime_explanation ?? '',
        recommendation: s.strategy ? {
          strategy: s.strategy.name ?? '',
          dte: s.strategy.target_dte ?? 0,
          legs: (s.strategy.legs ?? []).map((l: any) => ({
            action: l.action, qty: l.quantity, strike: l.strike,
            type: l.right === 'C' ? 'Call' : 'Put',
            expiry: l.expiry, price: l.premium ?? 0,
          })),
          max_profit: s.strategy.max_profit ?? 0,
          max_profit_net: s.strategy.max_profit_net,
          max_loss: s.strategy.max_loss ?? 0,
          max_loss_net: s.strategy.max_loss_net,
          break_even: Array.isArray(s.strategy.break_even) ? s.strategy.break_even : [],
          explanation: s.strategy.explanation ?? '',
        } : undefined,
      }));
      const updatedAt = raw.length > 0 ? raw[0].timestamp || '' : '';
      setAnalysis({ symbols, updated_at: updatedAt });
      // Wenn fuer einzelne Symbole ein Fehler kam, dem Nutzer zeigen — sonst
      // sieht er die Symbole einfach nicht und denkt, das System schlaeft.
      if (symbolErrors.length > 0) {
        setError(symbolErrors.join(' | '));
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Analyse');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const chartInitialisedRef = useRef(false);

  useEffect(() => {
    if (!chartInitialisedRef.current && analysis?.symbols?.length) {
      chartInitialisedRef.current = true;
      setChartOpen({ [analysis.symbols[0].symbol]: true });
    }
  }, [analysis]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await ibkrService.refreshAnalysis();
      await fetchAnalysis();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleExecute = async (symbol: string) => {
    setExecutingSymbol(symbol);
    try {
      const result = await ibkrService.executeSignal(symbol);
      if (result.error) {
        setError(result.error);
      } else {
        setError(null);
        await fetchAnalysis();
      }
    } catch (err: any) {
      setError(err.message || 'Ausfuehrung fehlgeschlagen');
    } finally {
      setExecutingSymbol(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Live-Analyse
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {analysis?.updated_at && (
            <Typography variant="body2" color="text.secondary">
              Aktualisiert: {analysis.updated_at}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Aktualisiere...' : 'Aktualisieren'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {(!analysis?.symbols || analysis.symbols.length === 0) ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Keine Analysedaten verfuegbar. Bitte IBKR verbinden und Analyse starten.</Typography>
        </Paper>
      ) : (
        analysis.symbols.map((sym) => (
          <Paper key={sym.symbol} sx={{ p: 3, mb: 3 }}>
            {/* Symbol Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {sym.symbol}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                @ {formatValueWithNet(sym.price, sym.price_net)}
              </Typography>
            </Box>

            {/* Regime, Score, IV */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Regime</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={REGIME_LABELS[sym.regime] || sym.regime}
                    color={REGIME_COLORS[sym.regime] || 'default'}
                    size="small"
                  />
                  {sym.regime_since_days != null && (
                    <Typography variant="body2" color="text.secondary">
                      seit {sym.regime_since_days} Tagen
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Score</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(sym.score ?? 0) > 0 ? '+' : ''}{(sym.score ?? 0).toFixed(2)}
                  {' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({sym.score > 0.3 ? 'bullish' : sym.score < -0.3 ? 'bearish' : 'neutral'})
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">IV Rank</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(sym.iv_rank ?? 0).toFixed(0)}%
                  {' '}
                  <Chip
                    label={sym.iv_rank > 50 ? 'HIGH' : 'LOW'}
                    size="small"
                    color={sym.iv_rank > 50 ? 'warning' : 'info'}
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">IV</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {(sym.iv ?? 0).toFixed(1)}%
                  {sym.iv_range && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}(Range: {(sym.iv_range?.[0] ?? 0).toFixed(1)}% - {(sym.iv_range?.[1] ?? 0).toFixed(1)}%)
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Chart Toggle */}
            <Box sx={{ mb: 2 }}>
              <Button
                type="button"
                size="small"
                startIcon={<ShowChart />}
                endIcon={chartOpen[sym.symbol] ? <ExpandLess /> : <ExpandMore />}
                onClick={() => toggleChart(sym.symbol)}
              >
                {chartOpen[sym.symbol] ? 'Chart ausblenden' : 'Chart anzeigen'}
              </Button>
              <Collapse in={!!chartOpen[sym.symbol]} unmountOnExit>
                <IBKRSymbolChart
                  symbol={sym.symbol}
                  recommendation={sym.recommendation}
                />
              </Collapse>
            </Box>

            {/* Explanation */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {sym.explanation}
              </Typography>
            </Paper>

            {/* Recommendation */}
            {sym.recommendation && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Empfehlung: {sym.recommendation.strategy} ({sym.recommendation.dte} DTE)
                </Typography>

                {sym.recommendation.legs.map((leg, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', mb: 0.5 }}>
                    {leg.action.toUpperCase()} {leg.qty}x {sym.symbol} {leg.expiry} ${leg.strike} {leg.type} @ {formatCurrency(leg.price)}
                  </Typography>
                ))}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                  <Typography variant="body2">
                    Max Gewinn: <strong>{formatValueWithNet(sym.recommendation.max_profit, sym.recommendation.max_profit_net)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Max Verlust: <strong>{formatValueWithNet(sym.recommendation.max_loss, sym.recommendation.max_loss_net)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Break-Even: <strong>{
                      sym.recommendation.break_even.length === 0
                        ? '–'
                        : sym.recommendation.break_even.map(formatCurrency).join(' / ')
                    }</strong>
                  </Typography>
                </Box>

                {sym.recommendation.explanation && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
                    {sym.recommendation.explanation}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleExecute(sym.symbol)}
                    disabled={executingSymbol === sym.symbol}
                  >
                    {executingSymbol === sym.symbol ? 'Wird ausgefuehrt...' : 'Ausfuehren'}
                  </Button>
                </Box>
              </Paper>
            )}
          </Paper>
        ))
      )}
    </Box>
  );
};

export default IBKRAnalysis;
